import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { collection, addDoc, onSnapshot, doc, deleteDoc, updateDoc, DocumentData, query } from "firebase/firestore";
import { db } from "services/firebase";
import { useAuth } from "./AuthContext";

export interface Expediente {
  start: string;
  end: string;
}

export interface Expedientes {
  [titulo: string]: Expediente;
}

export interface Device {
  id: string;
  name: string;
  status: "Aguardando" | "Operando" | "Desligado";
  days: string[];
  expedientes: Expedientes;
  token: string;
  exibicao?: string;
  titulo?: string;
  sobraLimpa?: boolean;
}

interface DeviceContextType {
  devices: Device[];
  adicionarDevice: (device: Omit<Device, "id">) => Promise<string>;
  removerDevice: (id: string) => Promise<void>;
  editarDevice: (id: string, dados: Partial<Omit<Device, "id">>) => Promise<void>;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const DeviceProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);

  useEffect(() => {
    if (!user?.uid) {
      setDevices([]);
      return;
    }

    const collectionPath = `clientes/${user.uid}/devices`;
    const q = query(collection(db, collectionPath));

    const unsub = onSnapshot(q, (snapshot) => {
      const list: Device[] = snapshot.docs.map((d) => {
        const data = d.data();
        if (data.lunchTime || data.dinnerTime) {
          data.expedientes = {};
          if (data.lunchTime?.start) {
            data.expedientes['Almoço'] = data.lunchTime;
          }
          if (data.dinnerTime?.start) {
            data.expedientes['Jantar'] = data.dinnerTime;
          }
          delete data.lunchTime;
          delete data.dinnerTime;
        }

        return {
          id: d.id,
          ...(data as Omit<Device, "id">),
        };
      });
      setDevices(list);
    });

    return () => unsub();
  }, [user]);

  const getCollectionRef = () => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return collection(db, `clientes/${user.uid}/devices`);
  };

  const getDocRef = (id: string) => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return doc(db, `clientes/${user.uid}/devices`, id);
  };

  const adicionarDevice = async (device: Omit<Device, "id">): Promise<string> => {
    const docRef = await addDoc(getCollectionRef(), device as DocumentData);
    return docRef.id;
  };

  const removerDevice = async (id: string): Promise<void> => {
    await deleteDoc(getDocRef(id));
  };

  const editarDevice = async (id: string, dados: Partial<Omit<Device, "id">>): Promise<void> => {
    await updateDoc(getDocRef(id), dados as DocumentData);
  };

  return (
    <DeviceContext.Provider value={{ devices, adicionarDevice, removerDevice, editarDevice }}>
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevices = (): DeviceContextType => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error("useDevices must be used within a DeviceProvider");
  }
  return context;
};
